import { MinusCircleOutlined } from '@ant-design/icons';
import { Button, Form, Input, Radio, Select } from 'antd';
import { Rule } from 'antd/es/form';
import { useState } from 'react';
import { ApplicantUserContactInformationInput, ApplicantUserPhoneInput } from '../../../../generated';
import { Country } from '../../../../types/country';
import StateSelect from '../../../shared/state-select';
import { EditProfile } from './edit-profile';

interface SelectOption {
  label: string;
  value: string;
}

const phoneTypes = [
  {
    label: 'Home',
    value: 'home'
  },
  {
    label: 'Work',
    value: 'work'
  },
  {
    label: 'Cell',
    value: 'cell'
  },
  {
    label: 'Other',
    value: 'other'
  }
];

interface Additionalphone {
  phoneType: string;
  phoneNumber: string;
}

interface ApplicantProfileContactEditProps {
  contactInformation: ApplicantUserContactInformationInput;
  onCancel: () => void;
  onSave: (contact: ApplicantUserContactInformationInput) => Promise<void>;
  countryData: Country[];
}

export const ApplicantProfileContactEdit: React.FC<ApplicantProfileContactEditProps> = (props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm<ApplicantUserContactInformationInput>();
  const [zipCodeRules, setZipCodeRules] = useState<Rule[]>([{ required: false, message: '' }]);
  const selectedCountry = Form.useWatch(['address', 'country'], form);

  const onCountryChange = (value: string) => {
    // Reset State/Province field when country changes
    form.setFieldsValue({
      address: {
        stateOrProvince: undefined
      }
    });

    switch (value) {
      case 'US':
      case 'CA':
        setZipCodeRules([
          { required: true, message: 'Please enter the ZIP/Postal code' },
          {
            pattern: /^\d{5}(?:[-\s]\d{4})?$/,
            message: 'Please enter a valid ZIP/Postal code'
          }
        ]);
        break;
      default:
        setZipCodeRules([{ required: false, message: '' }]);
        break;
    }
  };

  const [selectablePhoneTypes, setSelectablePhoneTypes] = useState<SelectOption[]>(phoneTypes);
  const [additionalphone, setAdditionalPhoneNumber] = useState<Additionalphone[]>([]);
  const onPhoneSelectChanged = (values: string, index: number) => {
    const newAdditionalPhone = [...additionalphone];
    newAdditionalPhone[index].phoneType = values;
    newAdditionalPhone[index].phoneNumber = '';

    const selectedPhoneTypes: string[] = [];
    newAdditionalPhone.forEach((phone: Additionalphone) => {
      selectedPhoneTypes.push(phone.phoneType);
    });

    const remainingPhoneTypes = selectablePhoneTypes.filter((type: SelectOption) => !selectedPhoneTypes.includes(type.value));
    setSelectablePhoneTypes(remainingPhoneTypes);
    setAdditionalPhoneNumber(newAdditionalPhone);
  };

  const ContactDetailsForm = (
    <Form
      className="w-[55%]"
      form={form}
      initialValues={props?.contactInformation}
      layout="vertical"
      onFinish={async (values) => {
        const contactInfo: ApplicantUserContactInformationInput = {
          address: values.address
        };
        contactInfo.phone = values.phone
          ?.filter((phone): phone is ApplicantUserPhoneInput => phone !== null)
          .map((phone: ApplicantUserPhoneInput) => {
            return {
              id: phone.id,
              phoneType: phone.phoneType,
              phoneNumber: phone.phoneNumber,
              isPrimary: phone.isPrimary
            };
          });

        contactInfo.emailAddress = values?.emailAddress;
        setIsSubmitting(true);
        await props?.onSave(contactInfo);
      }}
    >
      <p className="ant-form-item-label">Phone Numbers (Max 5)</p>
      <Form.List name={['phone']}>
        {(fields, { add, remove }) => {
          return (
            <div>
              {fields.map((field, index) => {
                return (
                  <div key={field.key} className="mb-4 flex">
                    <div className="inline-block align-top pr-2">
                      <MinusCircleOutlined
                        className="dynamic-delete-button"
                        onClick={() => {
                          remove(field.name);
                        }}
                      />
                    </div>
                    <div className="flex gap-2 pt-[3px] pl-2 border-l border-darkgrey">
                      <Form.Item name={[index, 'id']} hidden={true}>
                        <Input hidden={true} />
                      </Form.Item>
                      <Form.Item
                        name={[index, 'phoneType']}
                        validateTrigger={['onChange', 'onBlur']}
                        rules={[
                          {
                            required: true,
                            message: 'Phone Type is required'
                          }
                        ]}
                        label="Phone Type"
                      >
                        <Select
                          placeholder="Type"
                          className="w-[200px]"
                          onChange={(values) => {
                            onPhoneSelectChanged(values, index);
                          }}
                        >
                          {selectablePhoneTypes?.map((item: SelectOption) => (
                            <Select.Option key={item.value} value={item.value}>
                              {item.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        name={[index, 'phoneNumber']}
                        validateTrigger={['onChange', 'onBlur']}
                        rules={[
                          {
                            required: true,
                            message: 'Phone Number is required'
                          }
                        ]}
                        label="Phone Number"
                      >
                        <Input placeholder="Phone Number" maxLength={15} />
                        {/* <FormTags /> */}
                      </Form.Item>
                      <Form.Item name={[index, 'isPrimary']} valuePropName="checked">
                        <Radio
                          onChange={() => {
                            // unchecked all other radio buttons and set the current radio button to true
                            form.setFieldsValue({
                              phone: form.getFieldValue(['phone']).map((p: ApplicantUserPhoneInput, i: number) => {
                                return {
                                  ...p,
                                  isPrimary: i === index
                                };
                              })
                            });
                          }}
                          style={{
                            marginTop: '-5px',
                            paddingTop: '40px',
                            textAlign: 'justify',
                            width: 'Fixed (92px)',
                            height: 'Hug (22px)',
                            left: '892px',
                            gap: '8px',
                            top: '310px'
                          }}
                        >
                          Primary
                        </Radio>
                      </Form.Item>
                    </div>
                  </div>
                );
              })}
              {fields.length < 5 ? (
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => {
                      setAdditionalPhoneNumber([...additionalphone, { phoneType: '', phoneNumber: '' }]);
                      add();
                    }}
                    block
                    className="w-[500px] pr-2"
                  >
                    Add Additional Phone
                  </Button>
                </Form.Item>
              ) : (
                <></>
              )}
            </div>
          );
        }}
      </Form.List>
      <h2>Address</h2>
      <Form.Item rules={[{ required: true, message: 'Address Line 1 is required' }]} label="Address Line 1" name={['address', 'streetAddressLine1']}>
        <Input className="w-[400px]" placeholder="Address Line:1" />
      </Form.Item>
      <Form.Item label="Address Line 2" name={['address', 'streetAddressLine2']}>
        <Input className="w-[400px]" placeholder="Address Line:2" />
      </Form.Item>
      <Form.Item rules={[{ required: true, message: 'Country is required' }]} name={['address', 'country']} label="Country">
        <Select placeholder="Country" className="w-[200px]" onChange={(value) => onCountryChange(value)} showSearch optionFilterProp="children">
          {props?.countryData.map((item: Country) => (
            <Select.Option key={item.countryCode} value={item.countryCode}>
              {item.countryName}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <div className="flex w-full gap-2">
        <Form.Item label="City" name={['address', 'city']}>
          <Input placeholder="City" />
        </Form.Item>
        <StateSelect name={['address', 'stateOrProvince']} selectedCountry={selectedCountry} countryData={props?.countryData} />
        <Form.Item label="Zip / Postal Code" rules={zipCodeRules} name={['address', 'zipOrPostalCode']}>
          <Input maxLength={10} minLength={5} placeholder="Zip / Postal Code" />
        </Form.Item>
      </div>

      <div className="flex justify-end gap-2">
        <Button onClick={props?.onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button htmlType="submit" className="bg-[#3F4373] text-[#D9D9D9]" value={'save'} loading={isSubmitting}>
          Save
        </Button>
      </div>
    </Form>
  );

  return <EditProfile title="Edit Contact Details" form={ContactDetailsForm} />;
};
